import { X, Link2, Check, Copy } from 'lucide-react';
import { FaWhatsapp, FaFacebookF, FaTelegramPlane } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { useState } from 'react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  campaignTitle: string;
}

export function ShareModal({ isOpen, onClose, campaignId, campaignTitle }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Share URL — points to the API share endpoint which serves meta tags for bots
  // and redirects humans to the SPA campaign page
  const shortId = campaignId.substring(0, 8);
  const url = `${window.location.origin}/api/share/${shortId}`;
  const text = `🎯 "${campaignTitle}" — Ayuda con esta campaña de donación`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: <FaWhatsapp className="w-5 h-5" />,
      color: 'bg-[#25D366] hover:bg-[#1fb855]',
      onClick: () => window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n\n${url}`)}`, '_blank'),
    },
    {
      name: 'Facebook',
      icon: <FaFacebookF className="w-5 h-5" />,
      color: 'bg-[#1877F2] hover:bg-[#166fe5]',
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank'),
    },
    {
      name: 'X (Twitter)',
      icon: <FaXTwitter className="w-5 h-5" />,
      color: 'bg-black hover:bg-gray-800',
      onClick: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank'),
    },
    {
      name: 'Telegram',
      icon: <FaTelegramPlane className="w-5 h-5" />,
      color: 'bg-[#0088cc] hover:bg-[#0077b5]',
      onClick: () => window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank'),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-semibold text-gray-900 mb-1">Compartir campaña</h3>
        <p className="text-sm text-gray-500 mb-5 line-clamp-1">{campaignTitle}</p>

        {/* Social buttons */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {shareOptions.map((opt) => (
            <button
              key={opt.name}
              onClick={opt.onClick}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-colors ${opt.color}`}
            >
              {opt.icon}
              {opt.name}
            </button>
          ))}
        </div>

        {/* Copy link */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-600 truncate flex-1">{url}</span>
          <button
            onClick={handleCopy}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              copied ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
